/**
 * HeadToHeadScoreboard - V9 Navigation Update
 * 
 * CHANGES FROM ORIGINAL:
 * 1. Persistent "SCOREBOARD" header at top (matches Scoreboard V9)
 * 2. Tab bar: "← THE BOARD" | "SCOUT: {opponent}"
 * 3. Quick-switch chips: "vs alex", "vs brian", etc.
 * 
 * STATUS BAR LOGIC (unchanged):
 * - LIVE: "🔴 LIVE" (pulsing)
 * - PENDING: "⏳ Waiting for kickoff..."
 * - HISTORICAL/FINAL: "Final Results"
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useHeadToHeadData, getTracking } from '../../hooks/useHeadToHeadData';

// =============================================================================
// Icons
// =============================================================================

const BackIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
  </svg>
);

const RefreshIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
  </svg>
);

// =============================================================================
// Helpers
// =============================================================================

const formatStat = (stat) => {
  if (!stat) return '';
  const mapping = {
    'PASSING_YARDS': 'Pass Yds',
    'RUSHING_YARDS': 'Rush Yds',
    'RECEIVING_YARDS': 'Rec Yds',
    'RECEPTIONS': 'Rec',
    'PASSING_TDS': 'Pass TD',
    'RUSHING_TDS': 'Rush TD',
    'RECEIVING_TDS': 'Rec TD',
    'INTERCEPTIONS': 'INT',
    'COMPLETIONS': 'Comp',
    'ATTEMPTS': 'Att',
  };
  return mapping[stat] || stat.replace(/_/g, ' ');
};

const formatTimeAgo = (timestamp) => {
  if (!timestamp) return 'just now';
  const now = Date.now();
  const then = new Date(timestamp).getTime();
  const diffMin = Math.floor((now - then) / 60000);
  
  if (diffMin < 1) return 'just now';
  if (diffMin === 1) return '1 min ago';
  if (diffMin < 60) return `${diffMin} min ago`;
  return `${Math.floor(diffMin / 60)} hrs ago`;
};

// =============================================================================
// Sub-components
// =============================================================================

const PickCell = ({ pick, align = 'left' }) => {
  if (!pick) {
    return <div className={`text-slate-600 text-[13px] ${align === 'right' ? 'text-right' : 'text-left'}`}>—</div>;
  }
  
  const tracking = getTracking(pick);
  const dirColor = pick.direction === 'Above' ? 'text-emerald-400' : 'text-red-400';
  const alignClass = align === 'right' ? 'text-right' : 'text-left';
  
  return (
    <div className={alignClass}>
      <div className="text-[13px] leading-tight">
        <span className={`font-semibold ${dirColor}`}>{pick.direction}</span>
        <span className="text-white ml-1">{pick.line}</span>
      </div>
      <div className="text-[11px] text-slate-500">{pick.units}u</div>
      {pick.status === 'hit' && <div className="text-[11px] text-emerald-400 font-semibold">✓ HIT</div>}
      {pick.status === 'miss' && <div className="text-[11px] text-red-400 font-semibold">✗ MISS</div>}
      {pick.status === 'live' && tracking && (
        <div className={`text-[11px] font-semibold text-${tracking.color}-400`}>{tracking.text}</div>
      )}
    </div>
  );
};

const PlayerRow = ({ player, opponentName }) => {
  const allStats = new Set();
  player.yourTargets.forEach(t => allStats.add(t.stat));
  player.opponentTargets.forEach(t => allStats.add(t.stat));
  
  const statRows = Array.from(allStats).map(stat => {
    const yourPick = player.yourTargets.find(t => t.stat === stat);
    const oppPick = player.opponentTargets.find(t => t.stat === stat);
    const current = yourPick?.current ?? oppPick?.current ?? null;
    const isOpposite = yourPick && oppPick && yourPick.direction !== oppPick.direction;
    return { stat, yourPick, oppPick, current, isOpposite };
  });
  
  return (
    <div className="border-b border-slate-800">
      {statRows.map((row, idx) => (
        <div 
          key={`${player.name}-${row.stat}`}
          className={`grid grid-cols-[1fr_90px_50px_90px] items-start py-2.5 px-3 gap-1 ${row.isOpposite ? 'bg-amber-500/10' : ''}`}
        >
          {/* Column 1: Player Name (first row) + Stat */}
          <div className="min-w-0 pr-1">
            {idx === 0 && (
              <div className="text-[13px] font-bold text-white leading-tight">{player.name}</div>
            )}
            <div className="text-[10px] text-slate-500 uppercase">{formatStat(row.stat)}</div>
          </div>
          
          {/* Column 2: YOU */}
          <div className="text-right">
            <PickCell pick={row.yourPick} align="right" />
          </div>
          
          {/* Column 3: NOW */}
          <div className="text-center">
            {row.current !== null ? (
              <div className="text-lg font-mono font-bold text-white">{typeof row.current === 'number' ? row.current.toFixed(1) : row.current}</div>
            ) : (
              <span className="text-slate-700">—</span>
            )}
          </div>
          
          {/* Column 4: OPPONENT */}
          <div>
            <PickCell pick={row.oppPick} align="left" />
          </div>
        </div>
      ))}
    </div>
  );
};

const Section = ({ title, icon, players, opponentName }) => {
  if (!players || players.length === 0) return null;
  
  return (
    <>
      <div className="px-3 py-2 bg-slate-900 border-b border-slate-800 sticky top-[100px] z-10">
        <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
          {icon} {title}
        </span>
      </div>
      {players.map(p => (
        <PlayerRow key={p.name} player={p} opponentName={opponentName} />
      ))}
    </>
  );
};

const LoadingState = () => (
  <div className="min-h-screen bg-slate-950 flex items-center justify-center">
    <RefreshIcon className="w-8 h-8 text-slate-400 animate-spin" />
  </div>
);

const ErrorState = ({ error, onRetry }) => (
  <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
    <div className="text-center">
      <div className="text-red-400 font-bold mb-2">Unable to load</div>
      <div className="text-slate-500 text-sm mb-4">{error?.message}</div>
      <button onClick={onRetry} className="px-4 py-2 bg-slate-800 text-white rounded-lg min-h-[44px]">
        Try Again
      </button>
    </div>
  </div>
);

// =============================================================================
// Custom Hook: Fetch other competitors for quick-switch chips
// =============================================================================

const useCompetitors = (poolId, currentUserId) => {
  const [competitors, setCompetitors] = useState([]);
  
  useEffect(() => {
    if (!poolId) return;
    
    const fetchCompetitors = async () => {
      try {
        const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api/v1';
        const response = await fetch(`${API_BASE}/pools/${poolId}/leaderboard`, {
          headers: {
            'Content-Type': 'application/json',
            'X-User-ID': currentUserId,
          },
        });
        
        if (response.ok) {
          const data = await response.json();
          const entries = data.entries || data.leaderboard || [];
          // Filter out current user, keep only opponents
          const others = entries
            .filter(e => {
              const oddsUserId = String(e.user_id || e.userId || '').toLowerCase();
              const currentLower = String(currentUserId || '').toLowerCase();
              return oddsUserId !== currentLower;
            })
            .map(e => ({
              userId: e.user_id || e.userId,
              username: e.username || e.display_name || e.user_id || 'Unknown',
            }));
          setCompetitors(others);
        }
      } catch (err) {
        console.log('[H2H] Failed to fetch competitors for quick-switch:', err.message);
      }
    };
    
    fetchCompetitors();
  }, [poolId, currentUserId]);
  
  return competitors;
};

// =============================================================================
// Main Component
// =============================================================================

export default function HeadToHeadScoreboard() {
  const { poolId, opponentUserId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const currentUserId = user?.user_id || 'larry';
  
  const { data, loading, error, refetch, lastFetchTime } = useHeadToHeadData(
    poolId, opponentUserId, currentUserId
  );
  
  // Fetch other competitors for quick-switch chips
  const competitors = useCompetitors(poolId, currentUserId);
  
  // Filter out current opponent from quick-switch list
  const otherOpponents = competitors.filter(c => 
    String(c.userId).toLowerCase() !== String(opponentUserId).toLowerCase()
  );
  
  const handleBack = () => navigate(`/pools/${poolId}/leaderboard`);
  
  const handleSwitchOpponent = (newOpponentUserId) => {
    navigate(`/standings/${poolId}/vs/${newOpponentUserId}`);
  };
  
  if (loading && !data) return <LoadingState />;
  if (error && !data) return <ErrorState error={error} onRetry={refetch} />;
  if (!data) return <ErrorState error={{ message: 'No data' }} onRetry={refetch} />;
  
  const { you, opponent, snapshot, isLive, isAwaitingFirstRefresh, isHistoricalMode, sections } = data;
  const gap = you.score - opponent.score;
  
  // Determine if all players are resolved (no live or upcoming)
  const allResolved = sections.onField.length === 0 && sections.locker.length === 0;
  
  // Status bar logic
  const getStatusBar = () => {
    if (allResolved || isHistoricalMode) {
      return { text: 'Final Results', color: 'bg-slate-800', textColor: 'text-slate-400' };
    }
    if (isLive) {
      return { text: '🔴 LIVE', color: 'bg-red-500/20', textColor: 'text-red-400', pulse: true };
    }
    if (isAwaitingFirstRefresh) {
      return { text: '⏳ Waiting for kickoff...', color: 'bg-amber-500/10', textColor: 'text-amber-400' };
    }
    return { text: '⏳ Waiting for kickoff...', color: 'bg-amber-500/10', textColor: 'text-amber-400' };
  };
  
  const status = getStatusBar();
  
  return (
    <div className="min-h-screen bg-slate-950 text-white font-sans">
      {/* ============================================ */}
      {/* V9 HEADER - Persistent SCOREBOARD title */}
      {/* ============================================ */}
      <header className="sticky top-0 z-30 bg-slate-950/95 backdrop-blur border-b border-slate-800">
        <div className="flex items-center justify-between px-4 py-3">
          <button 
            onClick={handleBack}
            className="flex items-center gap-2 text-slate-400 hover:text-white min-h-[44px]"
          >
            <BackIcon />
            <span className="text-[13px] font-bold uppercase tracking-wider">War Room</span>
          </button>
          
          <div className="text-center">
            <h1 className="text-[15px] font-black text-white uppercase tracking-wider">Scoreboard</h1>
            <p className="text-[12px] text-slate-500">
              {competitors.length + 1} Coaches
            </p>
          </div>
          
          <div className="w-[44px]" />
        </div>
      </header>
      
      {/* ============================================ */}
      {/* V9 TAB BAR - "← THE BOARD" | "SCOUT: {name}" */}
      {/* ============================================ */}
      <div className="flex border-b border-slate-800">
        <button
          onClick={handleBack}
          className="flex-1 py-3 text-center text-[13px] font-bold text-slate-400 hover:text-white hover:bg-slate-800/50 transition-colors"
        >
          ← THE BOARD
        </button>
        <div className="flex-1 py-3 text-center text-[13px] font-bold text-white bg-slate-800 border-b-2 border-emerald-500">
          SCOUT: {opponent.name?.toUpperCase()}
        </div>
      </div>
      
      {/* ============================================ */}
      {/* SCORE HEADER - YOU vs OPPONENT */}
      {/* ============================================ */}
      <div className="bg-slate-900 border-b border-slate-800">
        <div className="flex items-center justify-center gap-3 py-3 px-3">
          <button onClick={handleBack} className="p-1 text-slate-400 min-w-[32px]">
            <BackIcon />
          </button>
          
          <div className="flex items-center gap-3">
            <div className="text-center">
              <div className="text-[10px] text-indigo-400 font-bold uppercase">You</div>
              <div className="text-2xl font-mono font-black">{you.score}</div>
            </div>
            
            <div className={`px-3 py-1 rounded-full ${gap >= 0 ? 'bg-emerald-500/20' : 'bg-red-500/20'}`}>
              <div className={`text-xl font-mono font-black ${gap >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                {gap >= 0 ? '+' : ''}{gap}
              </div>
            </div>
            
            <div className="text-center">
              <div className="text-[10px] text-slate-400 font-bold uppercase">{opponent.name}</div>
              <div className="text-2xl font-mono font-black">{opponent.score}</div>
            </div>
          </div>
          
          <div className="w-[32px]" />
        </div>
        
        {/* ============================================ */}
        {/* V9 QUICK-SWITCH CHIPS */}
        {/* ============================================ */}
        {otherOpponents.length > 0 && (
          <div className="flex gap-2 px-3 pb-3 overflow-x-auto">
            {otherOpponents.map(opp => (
              <button
                key={opp.userId}
                onClick={() => handleSwitchOpponent(opp.userId)}
                className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 rounded-full text-[12px] font-semibold text-slate-300 whitespace-nowrap transition-colors"
              >
                vs {opp.username}
              </button>
            ))}
          </div>
        )}
      </div>
      
      {/* STATUS BAR */}
      <div className={`px-3 py-2 border-b border-slate-800 text-center ${status.color}`}>
        <span className={`text-[11px] font-semibold ${status.textColor} ${status.pulse ? 'animate-pulse' : ''}`}>
          {status.text}
        </span>
      </div>
      
      {/* LAST UPDATE (only show in LIVE mode with deltas) */}
      {isLive && !allResolved && (
        <div className="px-3 py-1.5 bg-slate-900/80 border-b border-slate-800 text-center">
          <span className="text-[10px] text-slate-500">
            Last update: <span className="text-indigo-400">You {you.delta >= 0 ? '+' : ''}{you.delta}</span>
            {' · '}
            <span className="text-slate-300">{opponent.name} {opponent.delta >= 0 ? '+' : ''}{opponent.delta}</span>
            {' · '}{formatTimeAgo(lastFetchTime)}
          </span>
        </div>
      )}
      
      {/* COLUMN HEADERS */}
      <div className="grid grid-cols-[1fr_90px_50px_90px] items-center py-2 px-3 bg-slate-900 border-b border-slate-700 sticky top-[64px] z-20 gap-1">
        <div className="text-[10px] font-bold text-slate-500 uppercase">Player</div>
        <div className="text-[10px] font-bold text-indigo-400 uppercase text-right">You</div>
        <div className="text-[10px] font-bold text-slate-500 uppercase text-center">Now</div>
        <div className="text-[10px] font-bold text-slate-400 uppercase">{opponent.name}</div>
      </div>
      
      {/* SECTIONS */}
      <main className="pb-20">
        <Section title="On the Field" icon="⚡" players={sections.onField} opponentName={opponent.name} />
        <Section title="Locker Room" icon="🕐" players={sections.locker} opponentName={opponent.name} />
        <Section title="Finished" icon="✓" players={sections.finished} opponentName={opponent.name} />
        
        {sections.onField.length === 0 && sections.locker.length === 0 && sections.finished.length === 0 && (
          <div className="text-center py-12 text-slate-500 text-sm">No allocations to compare</div>
        )}
      </main>
      
      {/* FOOTER */}
      <div className="fixed bottom-0 left-0 right-0 bg-slate-900 border-t border-slate-700 px-4 py-3 safe-area-pb">
        <div className="flex justify-between text-[12px]">
          <div>
            <span className="text-indigo-400 font-semibold">You:</span>
            <span className="text-white font-bold ml-1">{you.units_remaining}u left</span>
          </div>
          <div>
            <span className="text-slate-400 font-semibold">{opponent.name}:</span>
            <span className="text-white font-bold ml-1">{opponent.units_remaining}u left</span>
          </div>
        </div>
      </div>
    </div>
  );
}
